// src/auth/auth0TokenSource.ts - the token provider's source while the office
// signs in through Auth0.
//
// The SDK keeps the access token in memory (no `cacheLocation` is configured)
// and renews it through a hidden-iframe authorize once it has expired. A forced
// renewal (the api refused the current token) skips the SDK's cache.
//
// Some failures are final: the identity provider says the operator has to sign
// in again, and no silent call can succeed until they do. The source reports
// those once through `onSessionOver`, so the AuthProvider drops it and the
// console stops asking: without that, a dead session would cost two authorize
// round trips on every request, forever, and the api's 401 would be the only
// thing the operator saw.
import type { GetTokenSilentlyOptions } from "@auth0/auth0-react";
import type { TokenSource } from "./tokenProvider";

export type SilentTokenCall = (
  options: GetTokenSilentlyOptions
) => Promise<string>;

/**
 * The SDK's `error` codes after which silent renewal cannot succeed: the
 * provider has no session to renew from, or needs the operator present.
 */
const SESSION_OVER_CODES: ReadonlySet<string> = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token",
  "invalid_grant",
]);

export const isSessionOverError = (err: unknown): boolean => {
  const code =
    err && typeof err === "object" && "error" in err
      ? (err as { error?: unknown }).error
      : undefined;
  return typeof code === "string" && SESSION_OVER_CODES.has(code);
};

export const createAuth0TokenSource = (
  getAccessTokenSilently: SilentTokenCall,
  onSessionOver: () => void
): TokenSource => {
  return async ({ forceRefresh }) => {
    try {
      return await getAccessTokenSilently({
        cacheMode: forceRefresh ? "off" : "on",
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH_AUDIENCE,
          scope: "openid profile email",
        },
      });
    } catch (err) {
      if (isSessionOverError(err)) onSessionOver();
      throw err;
    }
  };
};
